import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useState, useMemo, useContext } from 'react';

import Link from 'components/shared/link';
import MainContext from 'context/main';

import CategoryInfo from './categoryInfo';

const defaultCategory = 'Advertising & Monetization';

const Integrations = ({
  title,
  items,
  eventsDescription,
  cards,
  footerLink,
  isLimited,
  withBackground,
}) => {
  const {
    language: { locale },
  } = useContext(MainContext);

  const [selectedCategory, setSelectedCategory] = useState(defaultCategory);

  const categories = useMemo(
    () => [...new Set(items.map(({ description }) => description))],
    [items]
  );

  const itemsToRender = useMemo(() => {
    const filtered = items.filter(({ description }) => description === selectedCategory);
    return isLimited ? items.slice(0, 9) : filtered;
  }, [items, selectedCategory, isLimited]);

  const cardsData = useMemo(() => {
    if (!cards) return null;
    const selectedCat = cards.find(({ id }) => id === selectedCategory);
    const { info, events } = selectedCat;
    const eventsArray = events?.split(', ');
    return { info, eventsArray };
  }, [cards, selectedCategory]);

  return (
    <section
      className={clsx(
        'safe-paddings',
        withBackground && 'bg-gray-1 py-40 xl:py-30 lg:py-20 md:py-14'
      )}
    >
      <div className="container">
        <h2 className="text-new-black text-[56px] leading-denser font-medium tracking-tight-4 text-center xl:text-[44px] lg:text-[36px] md:text-[32px]">
          {title}
        </h2>
        {!isLimited && (
          <>
            <div className="relative mb-8">
              <ul
                className={clsx(
                  'scrollbar-hidden relative z-10 flex justify-between items-center mt-10 overflow-auto ',
                  locale === 'en' ? 'lg:gap-6' : 'xl:gap-6'
                )}
              >
                {cards.map(
                  ({ id: category, title }) =>
                    categories.includes(category) && (
                      <li key={category} className="relative">
                        <button
                          className={clsx('pb-6 font-medium text-lg whitespace-nowrap', {
                            'text-new-gray-1': selectedCategory !== category,
                            'text-secondary-2': selectedCategory === category,
                          })}
                          type="button"
                          onClick={() => setSelectedCategory(category)}
                        >
                          {title}
                          <span
                            className={clsx(
                              'absolute inset-x-0 bottom-[5px] h-[3px] rounded-[3px]',
                              {
                                'bg-secondary-2': selectedCategory === category,
                              }
                            )}
                            aria-hidden
                          />
                        </button>
                      </li>
                    )
                )}
              </ul>
              <div
                className="absolute inset-x-0 bottom-[5px] h-[3px] bg-[#eaedf6] rounded-[3px]"
                aria-hidden
              />
            </div>
            {cardsData && (
              <CategoryInfo
                eventsDescription={eventsDescription}
                selectedCategory={selectedCategory}
                info={cardsData.info}
                events={cardsData.eventsArray}
              />
            )}
          </>
        )}
        <ul
          className={clsx('grid-gap grid', {
            'grid-cols-4 mt-7 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1': !isLimited,
            'grid-cols-3 mt-16 lg:grid-cols-2 lg:mt-12 md:block md:gap-y-0 md:space-y-4 md:mt-8':
              isLimited,
          })}
        >
          {itemsToRender.map(({ logo, title, url }, index) => (
            <li className={clsx(isLimited && 'lg:last:hidden md:last:block')} key={index}>
              <Link
                className="before:inner-shadow group relative flex flex-col items-center rounded-2xl border border-gray-4 p-6 pb-5 text-center transition-colors duration-200 before:invisible before:absolute before:top-0 before:left-0 before:h-full before:w-full before:rounded-2xl before:opacity-0 before:transition-[opacity,visibility] before:duration-200 hover:border-transparent hover:before:visible hover:before:opacity-100 md:p-4"
                to={url}
              >
                <img
                  className="w-auto h-16 md:h-14"
                  src={logo.url}
                  alt=""
                  width={64}
                  height={64}
                  loading="lazy"
                />
                <h3
                  className={clsx({
                    'mt-5 text-base leading-snug text-new-gray-1/70 whitespace-nowrap': !isLimited,
                    'mt-4 font-bold text-xl md:mt-3': isLimited,
                  })}
                >
                  {title}
                </h3>
              </Link>
            </li>
          ))}
        </ul>
        {isLimited && footerLink && (
          <Link
            className="mx-auto mt-12 !block w-max xl:mt-8 lg:mt-7"
            to={footerLink.url}
            size="sm"
            theme="black-underline"
          >
            {footerLink.title}
          </Link>
        )}
      </div>
    </section>
  );
};

Integrations.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      logo: PropTypes.shape({ url: PropTypes.string.isRequired }).isRequired,
      title: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    })
  ).isRequired,
  eventsDescription: PropTypes.string.isRequired,
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      info: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string.isRequired,
          iconName: PropTypes.string.isRequired,
          text: PropTypes.string.isRequired,
        })
      ),
      events: PropTypes.string,
    })
  ),
  footerLink: PropTypes.shape({
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  isLimited: PropTypes.bool,
  withBackground: PropTypes.bool,
};

Integrations.defaultProps = {
  cards: null,
  footerLink: null,
  isLimited: false,
  withBackground: false,
};

export default Integrations;
